'use client';

import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { EnhancedInput } from '@/components/ui/enhanced-input';
import { Checkbox } from '@/components/ui/checkbox';
import { CreditCard, Lock, Loader2 } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { getCompanySettings } from '@/lib/api';

export interface PayArcTokenizationDetails {
  payarcChargeId?: string;
  payarcTokenId?: string;
  payarcCustomerId?: string;
  expMonth?: string;
  expYear?: string;
}

interface PayArcTokenizationProps {
  companyId: string;
  amount: number;
  onTokenCreated: (tokenId: string, last4: string, brand: string, details?: PayArcTokenizationDetails) => void;
  onError: (error: string) => void;
  savePaymentMethod?: boolean;
  initialCardholderName?: string;
  customerEmail?: string;
}

export function PayArcTokenization({
  companyId,
  amount,
  onTokenCreated,
  onError,
  savePaymentMethod: initialSavePaymentMethod = false,
  initialCardholderName = '',
  customerEmail,
}: PayArcTokenizationProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [expMonth, setExpMonth] = useState('');
  const [expYear, setExpYear] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardholderName, setCardholderName] = useState(initialCardholderName);
  const [zipCode, setZipCode] = useState('');
  const [savePaymentMethod, setSavePaymentMethod] = useState(initialSavePaymentMethod);
  const [useHostedFields, setUseHostedFields] = useState(false);
  const hostedFieldsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Check if we should use hosted fields
    checkHostedFieldsAvailability();
  }, [companyId]);

  useEffect(() => {
    setCardholderName(initialCardholderName);
  }, [initialCardholderName]);

  const checkHostedFieldsAvailability = async () => {
    try {
      const settings = await getCompanySettings(companyId);
      const payarcConfig = settings?.paymentGateways?.providers?.payarc;

      // Check if hosted fields are available and preferred
      if (payarcConfig?.features?.hostedFieldsAvailable) {
        setUseHostedFields(true);
        initializeHostedFields();
      }
    } catch (error) {
      console.error('Failed to check PayArc configuration:', error);
    }
  };

  const initializeHostedFields = () => {
    // Initialize PayArc hosted fields if available
    // This would load PayArc's JavaScript SDK and create secure iframe fields
    // Placeholder for actual implementation when PayArc provides hosted fields SDK
    console.log('PayArc hosted fields would be initialized here');
  };

  const formatCardNumber = (value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Add spaces every 4 digits
    const formatted = digits.match(/.{1,4}/g)?.join(' ') || digits;
    return formatted;
  };

  const formatExpiry = (month: string, year: string) => {
    // Ensure month is 2 digits
    const formattedMonth = month.padStart(2, '0');
    // Ensure year is 2 or 4 digits
    const formattedYear = year.length === 2 ? `20${year}` : year;
    return { month: formattedMonth, year: formattedYear };
  };

  const validateCard = () => {
    // Basic card validation
    const cleanCardNumber = cardNumber.replace(/\s/g, '');

    if (cleanCardNumber.length < 13 || cleanCardNumber.length > 19) {
      onError('Invalid card number');
      return false;
    }

    const { month, year } = formatExpiry(expMonth, expYear);
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    const expYearNum = parseInt(year);
    const expMonthNum = parseInt(month);

    if (expMonthNum < 1 || expMonthNum > 12) {
      onError('Invalid expiration month');
      return false;
    }

    if (expYearNum < currentYear || (expYearNum === currentYear && expMonthNum < currentMonth)) {
      onError('Card has expired');
      return false;
    }

    if (cvv.length < 3 || cvv.length > 4) {
      onError('Invalid CVV');
      return false;
    }

    if (zipCode.length < 5) {
      onError('Invalid ZIP code');
      return false;
    }

    return true;
  };

  const handleTokenize = async () => {
    if (!validateCard()) return;

    setIsLoading(true);

    try {
      const cleanCardNumber = cardNumber.replace(/\s/g, '');
      const { month, year } = formatExpiry(expMonth, expYear);

      // Call tokenization endpoint
      const response = await fetch('/api/payments/payarc/tokenize', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          companyId,
          cardNumber: cleanCardNumber,
          expMonth: month,
          expYear: year,
          cvv,
          name: cardholderName,
          zipCode,
          amount, // Include the actual amount to charge
          customerEmail: customerEmail || '',
          savePaymentMethod, // Include save payment method preference
        }),
      });

      const data = await response.json();

      if (data.success) {
        const details: PayArcTokenizationDetails = {
          payarcChargeId: data.payarcChargeId,
          payarcTokenId: data.payarcTokenId,
          payarcCustomerId: data.payarcCustomerId,
          expMonth: data.expMonth ? String(data.expMonth).padStart(2, '0') : month,
          expYear: data.expYear ? String(data.expYear) : year,
        };
        onTokenCreated(data.tokenId, data.last4, data.brand, details);
      } else if (data.useHosted) {
        // If hosted tokenization is required, show message
        onError('Please use the secure payment form below');
        setUseHostedFields(true);
        initializeHostedFields();
      } else {
        onError(data.error || 'Failed to tokenize card');
      }
    } catch (error: any) {
      console.error('Tokenization error:', error);
      onError('Failed to process card information');
    } finally {
      setIsLoading(false);
    }
  };

  // Detect card brand from number
  const detectCardBrand = (number: string) => {
    const cleanNumber = number.replace(/\s/g, '');
    if (/^4/.test(cleanNumber)) return 'Visa';
    if (/^5[1-5]/.test(cleanNumber)) return 'Mastercard';
    if (/^3[47]/.test(cleanNumber)) return 'American Express';
    if (/^6(?:011|5)/.test(cleanNumber)) return 'Discover';
    return 'Card';
  };

  if (useHostedFields) {
    // Render hosted fields container
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4">
          <Label className="text-base font-medium">Payment Information</Label>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Lock className="h-4 w-4" />
            Secure Payment
          </div>
        </div>

        {/* Hosted fields iframe would be inserted here */}
        <div ref={hostedFieldsRef} className="p-4 border rounded-lg bg-gray-50">
          <p className="text-sm text-muted-foreground text-center">
            PayArc secure payment form would load here
          </p>
        </div>

        <div className="flex items-center justify-between pt-4">
          <span className="text-sm text-muted-foreground">Amount to charge:</span>
          <span className="font-semibold text-lg">{formatCurrency(amount)}</span>
        </div>
      </div>
    );
  }

  // Render manual card input form
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <Label className="text-base font-medium">Payment Information</Label>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Lock className="h-4 w-4" />
          Secure Payment
        </div>
      </div>

      {/* Cardholder name field hidden - using initialCardholderName prop instead */}
      {/* <div>
        <Label htmlFor="cardholder-name">Cardholder Name</Label>
        <EnhancedInput
          id="cardholder-name"
          placeholder="John Doe"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
          disabled={isLoading}
        />
      </div> */}

      <div>
        <Label htmlFor="card-number">Card Number</Label>
        <div className="relative">
          <EnhancedInput
            id="card-number"
            placeholder="1234 5678 9012 3456"
            value={cardNumber}
            onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
            maxLength={19}
            disabled={isLoading}
            className="pl-10"
          />
          <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
            {detectCardBrand(cardNumber)}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label htmlFor="exp-date">Expiration</Label>
          <div className="grid grid-cols-2 gap-2">
            <EnhancedInput
              id="exp-month"
              placeholder="MM"
              value={expMonth}
              onChange={(e) => setExpMonth(e.target.value.replace(/\D/g, '').slice(0, 2))}
              maxLength={2}
              disabled={isLoading}
            />
            <EnhancedInput
              id="exp-year"
              placeholder="YY"
              value={expYear}
              onChange={(e) => setExpYear(e.target.value.replace(/\D/g, '').slice(0, 2))}
              maxLength={2}
              disabled={isLoading}
            />
          </div>
        </div>

        <div>
          <Label htmlFor="cvv">CVV</Label>
          <EnhancedInput
            id="cvv"
            placeholder="123"
            type="password"
            value={cvv}
            onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
            maxLength={4}
            disabled={isLoading}
          />
        </div>

        <div>
          <Label htmlFor="zip">ZIP Code</Label>
          <EnhancedInput
            id="zip"
            placeholder="12345"
            value={zipCode}
            onChange={(e) => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 9))}
            maxLength={9}
            disabled={isLoading}
          />
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t">
        <span className="text-sm text-muted-foreground">Amount to charge:</span>
        <span className="font-semibold text-lg">{formatCurrency(amount)}</span>
      </div>

      {/* Save payment method checkbox */}
      <div className="flex items-center space-x-2">
        <Checkbox
          id="save-payment-method"
          checked={savePaymentMethod}
          onCheckedChange={(checked) => setSavePaymentMethod(checked === true)}
          disabled={isLoading}
        />
        <label
          htmlFor="save-payment-method"
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
        >
          Save payment method for future payments
        </label>
      </div>

      <Button
        onClick={handleTokenize}
        disabled={isLoading || !cardNumber || !expMonth || !expYear || !cvv || !zipCode}
        className="w-full"
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <Lock className="mr-2 h-4 w-4" />
            Secure Pay {formatCurrency(amount)}
          </>
        )}
      </Button>

      <div className="space-y-1">
        <p className="text-xs text-center text-muted-foreground">
          Your payment information is encrypted and secure.
        </p>
        <p className="text-xs text-center text-muted-foreground">
          Powered by PayArc
        </p>
      </div>
    </div>
  );
}
